import type { Client } from '@rhitm-incubator/oidc-manager';

export type Trial = {
  name: string;
  trialLink: string;
  startDate: Date;
  expireDate: Date;
  isActive: boolean;
  isAvailable: boolean;
  isCooldown: boolean;
  trialId: string;
  linkCategory?: string;
  daysLeft: number;
  total: number;
}

interface ProductTrialsServiceParams {
  statusEndpoint: string;
  offersEndpoint: string;
  user: NonNullable<Client['user']>;
}

export class ProductTrialsService {
  statusEndpoint: string;
  offersEndpoint: string;
  user: NonNullable<Client['user']>;

  constructor(params: ProductTrialsServiceParams) {
    this.statusEndpoint = params.statusEndpoint;
    this.offersEndpoint = params.offersEndpoint;
    this.user = params.user;
  }

  getStatus() {
    return fetch(this.statusEndpoint, {
      headers: {
        'Authorization': `Bearer ${this.user.access_token}`,
        'Content-Type': 'application/json',
      }
    });
  }

  getOffers() {
    return fetch(this.offersEndpoint);
  }

  async getTrials() {
    const [statusRes, offersRes] = await Promise.allSettled([
      this.getStatus(),
      this.getOffers(),
    ]);

    if (statusRes.status !== 'fulfilled' || offersRes.status !== 'fulfilled') {
      throw new Error('getTrials resulted in an error.');
    }

    const status = await statusRes.value.json();
    const offers = await offersRes.value.json();

    return this._mapResponsesToTrials(status, offers);
  }

  private _mapResponsesToTrials(statusRes: any, drupalRes: any) {
    // map drupal trials to offers
    const offersHash = new Map<any, any>();

    for (const offer of statusRes.payload.results) {
      const trial = drupalRes.data.find((d: any) => d.attributes.field_id === offer.trial.id);
      if (!trial) { continue; }
      offersHash.set(offer, trial);
    }

    const trials: Trial[] = [...offersHash.entries()]
      .map(([offer, trial]) => {
        // Convert the trial's startDate and expireDate to Date objects
        const startDate = new Date(offer.status.startDate);
        const expireDate = new Date(offer.status.expireDate);
        // Calculate the total number of days for the trial
        const total = Math.ceil((expireDate.getTime() - startDate.getTime()) / (1000 * 3600 * 24));
        // Get today's date
        const today = new Date();
        // Calculate the number of days left
        const daysLeft = Math.ceil((expireDate.getTime() - today.getTime()) / (1000 * 3600 * 24));

        return {
          name: trial.attributes.field_title,
          trialId: trial.attributes.field_id,
          trialLink: trial.attributes.product_trial_node_url,
          startDate,
          expireDate,
          isActive: offer.status.isActive,
          isCooldown: offer.status.isCooldown,
          isAvailable: offer.status.isAvailable,
          daysLeft,
          total
        };
      })
      // sort all active trials and by start date
      .sort((a, b) => {
        if (a.isActive && !b.isActive) { return -1; }
        if (!a.isActive && b.isActive) { return 1; }
        return b.startDate.getTime() - a.startDate.getTime();
      });

    return trials;
  }
}
